import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, onSnapshot, updateDoc, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { cacheHelpers } from './utils/campaignCache';
import cacheManager from './utils/cacheManager';

const UserContext = createContext();

const SELECTED_COMPANY_KEY = 'selectedCompanyId';
const SESSION_UID_KEY = 'sf_session_uid';
/** Max campaigns loaded in context (avoids loading thousands on search/campaigns pages) */
const CAMPAIGNS_INITIAL_LIMIT = 20;

/** Clear all user-scoped session data (localStorage caches, selectedCompany, etc.) */
const purgeSessionData = () => {
  localStorage.removeItem(SELECTED_COMPANY_KEY);
  localStorage.removeItem(SESSION_UID_KEY);
  sessionStorage.removeItem('Auth Token');
  cacheManager.clearAllCaches();
};

/** Normalize campaign createdAt for sorting (Firestore Timestamp or Date) */
const campaignCreatedAtMs = (c) => {
  if (!c?.createdAt) return 0;
  const t = c.createdAt;
  if (typeof t?.toMillis === 'function') return t.toMillis();
  if (typeof t?.seconds === 'number') return t.seconds * 1000;
  return new Date(t).getTime();
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsReady, setCampaignsReady] = useState(false); // true after first campaigns snapshot or cache
  const [agencyId, setAgencyId] = useState(null);
  // profileError: non-null when the user doc / workspace failed to load despite a valid auth
  // session. Consumers (PrivateRoute) show a retry screen instead of kicking to login.
  const [profileError, setProfileError] = useState(null);
  const navigate = useNavigate();

  // Optimistic creates: campaigns we prepended that may not be in the latest Firestore snapshot yet (cache/consistency)
  const pendingCampaignsRef = useRef(new Map());
  const campaignsListenerCompanyRef = useRef(null);
  // Refs to unsubscribe previous listeners (prevents leaks on workspace switch)
  const campaignsUnsubRef = useRef(null);
  const campaignsRetryRef = useRef(null); // retry timer for failed campaigns listener
  const workspaceUnsubRef = useRef(null); // workspace onSnapshot listener

  // Subscriptions retired. openUpgradeModal kept as a no-op shim so legacy call
  // sites do not need to be removed in lockstep.
  const openUpgradeModal = () => {};

  const onboardingComplete = userProfile ? userProfile.onboardingComplete !== false : null;

  // Game credits: how many one-time publish credits this workspace has
  const gameCredits = businessProfile?.gameCredits ?? 0;

  // Admin detection — mirrors src/components/AdminRoute.jsx so editor / FileUpload
  // can conditionally show advanced UI to admins only without a route guard.
  const _isLocalhostOrDev = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'smilesflowdev-1514f.web.app'
  );
  const _hasAdminRole = userProfile?.role === 'admin';
  const _isAdminEmail = userProfile?.email === 'support@smilesflow.com' || user?.email === 'support@smilesflow.com';
  const isAdmin = _hasAdminRole || (_isLocalhostOrDev && _isAdminEmail);

  // Pay-as-you-publish: every workspace gets one free publish slot. Beyond that, a
  // credit is required. The free slot is derived from campaigns rather than a counter
  // so pause/archive auto-reclaims it. Server-side publishGame is the source of truth;
  // this flag is a UI pre-check.
  const freeSlotInUse = (campaigns || []).some(
    (c) => c.publishedFree === true && (c.status === 'published' || c.systemPaused === true)
  );
  const canPublish = gameCredits > 0 || !freeSlotInUse;

  // True when user is locked out: not the owner AND activeUserIds is set AND user is not in it.
  // Used by Layout.jsx to show LockedOutScreen for Engage-plan users beyond the seat limit.
  const isLockedOut =
    !isOwner &&
    Array.isArray(businessProfile?.activeUserIds) &&
    businessProfile.activeUserIds.length > 0 &&
    user?.uid != null &&
    !businessProfile.activeUserIds.includes(user.uid);

  const getStudioRedirectPath = () => {
    if (loading) return null;
    if (!user) return '/studio/login';
    if (userProfile?.onboardingComplete === false) return '/studio/setup';
    return '/studio/dashboard';
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      // Only set loading true if this is the initial auth check
      if (user === null && userProfile === null) {
        setLoading(true);
      }

      if (currentUser) {
        // Detect user change: compare against both React state AND localStorage UID
        // This catches cross-session bleed (browser restart) and mid-session switches
        const previousUid = user?.uid || localStorage.getItem(SESSION_UID_KEY);
        const userChanged = previousUid && previousUid !== currentUser.uid;

        if (userChanged) {
          purgeSessionData();
          // Reset React state (but don't null out user — we're about to set the new one)
          setUserProfile(null);
          setBusinessProfile(null);
          setIsOwner(false);
          setCampaigns([]);
          setCampaignsReady(false);
          setAgencyId(null);
        }

        // Stamp the current user's UID into localStorage for cross-session detection
        localStorage.setItem(SESSION_UID_KEY, currentUser.uid);
        setUser(currentUser);

        if (userChanged) {
          await loadUserProfile(currentUser.uid);
        } else if (!userProfile) {
          // Only load profile if we genuinely don't have one yet (initial load).
          // Token refreshes fire onAuthStateChanged but don't need a full profile reload
          // since the workspace onSnapshot listener is already active.
          await loadUserProfile(currentUser.uid);
        }
      } else {
        resetUserState();
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional empty deps for auth init
  }, []);

  // Re-sync auth when tab becomes visible (avoids "signed out" when returning via browser back / bfcache)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const currentUser = auth.currentUser;
      if (currentUser && !user) {
        // Tab restored from bfcache with no React user state — re-hydrate without
        // setting loading=true (PrivateRoute already shows a loading screen when
        // user && !userProfile, so we don't need the loading flag to prevent a
        // flash redirect).
        setUser(currentUser);
        if (!userProfile) loadUserProfile(currentUser.uid);
      } else if (!currentUser && user) {
        resetUserState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user, userProfile]);

  // onIdTokenChanged fires when the auth token is refreshed, revoked, or the user is
  // disabled. This is the authoritative signal for "token is no longer valid" —
  // onAuthStateChanged only fires on sign-in/out, not mid-session revocation.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      if (!currentUser) return; // sign-out already handled by onAuthStateChanged
      try {
        // Force-refresh surfaces revocation errors synchronously so we can react.
        await currentUser.getIdToken(/* forceRefresh */ false);
      } catch (err) {
        const code = err?.code || '';
        if (TERMINAL_AUTH_ERRORS.has(code)) {
          await signOut(auth).catch(() => {});
          resetUserState();
          navigate('/studio/login', { replace: true });
        }
        // Transient errors: leave the session alone; the next real request will retry.
      }
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auth listener lifecycle matches provider
  }, []);

  // Retry loading profile when user is set but profile is null (e.g. after signup, dashboard loaded before profile)
  const profileRetryRef = useRef(0);
  useEffect(() => {
    if (!user || userProfile || loading) {
      if (userProfile) profileRetryRef.current = 0; // reset on success
      return;
    }
    const attempt = profileRetryRef.current;
    const delay = Math.min(500 * Math.pow(2, attempt), 8000); // 500ms, 1s, 2s, 4s, 8s cap
    const t = setTimeout(() => {
      profileRetryRef.current = attempt + 1;
      loadUserProfile(user.uid);
    }, delay);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadUserProfile is stable, omit to avoid loop
  }, [user, userProfile, loading]);

  // Add effect to check localStorage on initial load
  useEffect(() => {
    const loadSelectedCompany = async () => {
      const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
      if (selectedCompanyId && user && userProfile?.companyId) {
        // Only load if the stored workspace belongs to this user (own workspace or child workspace)
        if (selectedCompanyId === userProfile.companyId) {
          // Same as user's own workspace — skip, loadUserProfile handles it
          return;
        }
        const companyDoc = await getDoc(doc(db, 'workspace', selectedCompanyId));
        if (companyDoc.exists()) {
          const data = companyDoc.data();
          // Validate: workspace must be a child of this user's workspace (agency→client)
          if (data.parentCompany === userProfile.companyId) {
            // Validate members access (owner or unrestricted or in members list)
            if (!data.members || data.members.length === 0 || data.members.includes(user.uid)) {
              setBusinessProfile({ id: companyDoc.id, ...data });
            } else {
              // Check if agency owner (always has access)
              const agencyDoc = await getDoc(doc(db, 'workspace', userProfile.companyId));
              if (agencyDoc.exists() && agencyDoc.data().ownerId === user.uid) {
                setBusinessProfile({ id: companyDoc.id, ...data });
              } else {
                localStorage.removeItem(SELECTED_COMPANY_KEY);
              }
            }
          } else {
            // Stale/foreign workspace — clear it
            localStorage.removeItem(SELECTED_COMPANY_KEY);
          }
        } else {
          localStorage.removeItem(SELECTED_COMPANY_KEY);
        }
      }
    };

    loadSelectedCompany();
  }, [user, userProfile]); // Depend on user + userProfile to ensure we have authentication and ownership info

  useEffect(() => {
    if (user?.companyId) {
      setAgencyId(user.companyId);
    } else if (businessProfile?.id && !businessProfile.parentCompany) {
      // If businessProfile is loaded and it's not a client (no parentCompany)
      setAgencyId(businessProfile.id);
    }
  }, [user, businessProfile]);

  const resetUserState = () => {
    setUser(null);
    setUserProfile(null);
    setBusinessProfile(null);
    setIsOwner(false);
    setCampaigns([]);
    setCampaignsReady(false);
    setAgencyId(null);
    setProfileError(null);
    purgeSessionData();
    // Clean up workspace listener
    if (workspaceUnsubRef.current) {
      workspaceUnsubRef.current();
      workspaceUnsubRef.current = null;
    }
    // Clean up campaigns retry timer
    if (campaignsRetryRef.current) {
      clearTimeout(campaignsRetryRef.current);
      campaignsRetryRef.current = null;
    }
  };

  // Terminal auth errors: the user's token/account is genuinely invalid and a signout
  // is the correct response. Everything else (network blips, transient permission-denied,
  // Firestore internal errors) should be retried, not logged out.
  const TERMINAL_AUTH_ERRORS = new Set([
    'auth/user-disabled',
    'auth/user-token-expired',
    'auth/user-not-found',
    'auth/invalid-user-token',
    'auth/id-token-revoked',
    'auth/id-token-expired',
    'auth/requires-recent-login',
  ]);

  const loadUserProfile = async (userId) => {
    setProfileError(null);
    const fetchWithRetry = async (retries = 4) => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userSnapshot = await getDoc(userDocRef);

        if (userSnapshot.exists()) {
          setProfileError(null);
          const userData = userSnapshot.data();
          setUserProfile(userData);
          
          // Determine which company to use — validate stored selection belongs to this user
          let companyIdToUse = userData.companyId;
          const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
          if (selectedCompanyId && selectedCompanyId !== userData.companyId) {
            // Validate: selected workspace must be a child of the user's workspace
            try {
              const selectedDoc = await getDoc(doc(db, 'workspace', selectedCompanyId));
              if (selectedDoc.exists() && selectedDoc.data().parentCompany === userData.companyId) {
                companyIdToUse = selectedCompanyId;
              } else {
                // Stale or foreign workspace — clear it
                localStorage.removeItem(SELECTED_COMPANY_KEY);
              }
            } catch {
              localStorage.removeItem(SELECTED_COMPANY_KEY);
            }
          }
          
          if (companyIdToUse) {
            const businessDocRef = doc(db, 'workspace', companyIdToUse);

            // OPTIMIZATION: Show UI immediately with minimal profile + cached campaigns (avoids skeleton flash)
            const cachedCampaigns = cacheHelpers.getCachedUserCampaigns(companyIdToUse);
            setBusinessProfile({ id: companyIdToUse });
            setCampaigns(Array.isArray(cachedCampaigns) ? cachedCampaigns : []);
            setCampaignsReady(Array.isArray(cachedCampaigns)); // ready if we had cache, else wait for listener
            setLoading(false);

            // Clean up previous workspace listener to prevent leaks on re-auth / token refresh
            if (workspaceUnsubRef.current) {
              workspaceUnsubRef.current();
              workspaceUnsubRef.current = null;
            }

            workspaceUnsubRef.current = onSnapshot(businessDocRef, async (snapshot) => {
              if (snapshot.exists()) {
                const businessData = { id: snapshot.id, ...snapshot.data() };
                setBusinessProfile(businessData);
                setIsOwner(businessData.ownerId === userId);

                // OPTIMIZATION: Setup campaigns listener with caching
                setupOptimizedCampaignsListener(businessData.id);

                setLoading(false);
              } else {
                retryOrError();
              }
            }, (err) => {
              // Workspace deleted or permissions revoked — swallow gracefully
              console.warn('Workspace listener lost access (likely deleted):', err.code);
            });
          } else {
            retryOrError();
          }
        } else {
          // User doc doesn't exist. Retry a few times (new-signup races where the
          // users/{uid} doc is written just after auth completes), then surface
          // a recoverable error — do NOT signOut just because the doc is missing.
          if (retries > 1) {
            setTimeout(() => fetchWithRetry(retries - 1), 1500);
          } else {
            setLoading(false);
            setProfileError({ code: 'profile-missing', message: "We couldn't find your account profile." });
          }
        }
      } catch (error) {
        const code = error?.code || '';
        const attempt = 5 - retries;
        console.error(`Error loading user profile (attempt ${attempt}): ${code || error?.message}`);

        if (TERMINAL_AUTH_ERRORS.has(code)) {
          // Token really is invalid — sign the user out cleanly.
          setLoading(false);
          await signOut(auth).catch(() => {});
          resetUserState();
          navigate('/studio/login', { replace: true });
          return;
        }

        if (retries <= 1) {
          // Out of retries on a transient error. Show a retryable error screen
          // rather than kicking to login — auth is still valid.
          setLoading(false);
          setProfileError({ code: code || 'profile-load-failed', message: 'Could not load your workspace.' });
          return;
        }

        // Exponential backoff: 500ms, 1s, 2s, 4s (capped).
        const delay = Math.min(500 * Math.pow(2, attempt - 1), 4000);
        setTimeout(() => fetchWithRetry(retries - 1), delay);
      }
    };

    const retryOrError = () => {
      setLoading(false);
    };

    await fetchWithRetry();
  };

  // Public: retry profile load without reloading the page (used by ProfileLoadError screen)
  const retryLoadProfile = () => {
    if (!auth.currentUser) return;
    setProfileError(null);
    setLoading(true);
    loadUserProfile(auth.currentUser.uid);
  };

  // OPTIMIZATION: Enhanced campaigns listener with caching (limit avoids loading thousands)
  const setupOptimizedCampaignsListener = (companyId, { force = false } = {}) => {
    // Skip teardown/re-setup if the listener is already active for the same company
    // (workspace onSnapshot fires on every workspace update — no need to recreate campaigns listener each time)
    if (!force && campaignsUnsubRef.current && campaignsListenerCompanyRef.current === companyId) {
      return;
    }

    // Clear any pending retry timer
    if (campaignsRetryRef.current) {
      clearTimeout(campaignsRetryRef.current);
      campaignsRetryRef.current = null;
    }

    // Clean up previous listener to prevent leaks on workspace switch
    if (campaignsUnsubRef.current) {
      campaignsUnsubRef.current();
      campaignsUnsubRef.current = null;
    }
    // When switching company, clear pending so we don't merge old company's optimistic creates
    if (campaignsListenerCompanyRef.current !== null && campaignsListenerCompanyRef.current !== companyId) {
      pendingCampaignsRef.current.clear();
      setCampaignsReady(false);
    }
    campaignsListenerCompanyRef.current = companyId;

    // Check cache first (but don't use cache if we have pending – they'd be missing)
    const hasPending = pendingCampaignsRef.current.size > 0;
    const cachedCampaigns = hasPending ? null : cacheHelpers.getCachedUserCampaigns(companyId);
    if (cachedCampaigns) {
      setCampaigns(cachedCampaigns);
      setCampaignsReady(true);
    } else if (!hasPending) {
      // Only reset campaignsReady if there are no pending campaigns either
      // (pending campaigns are already in state from prependCampaign — don't flash skeleton)
      setCampaignsReady(false);
    }

    const campaignsRef = collection(db, 'campaign');
    const campaignsQuery = query(
      campaignsRef,
      where("companyId", "==", companyId),
      orderBy("createdAt", "desc"),
      limit(CAMPAIGNS_INITIAL_LIMIT)
    );

    campaignsUnsubRef.current = onSnapshot(campaignsQuery, (snapshot) => {
      const fromSnapshot = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const snapshotIds = new Set(fromSnapshot.map(c => c.id));

      // Merge in optimistically added campaigns that aren't in this snapshot yet (avoids cache/consistency overwriting new games)
      const pending = pendingCampaignsRef.current;
      const pendingForCompany = [];
      pending.forEach((entry, id) => {
        if (entry.companyId === companyId && !snapshotIds.has(id)) {
          pendingForCompany.push(entry.campaign);
        } else if (snapshotIds.has(id)) {
          pending.delete(id);
        }
      });
      const campaignsData = pendingForCompany.length === 0
        ? fromSnapshot
        : [...pendingForCompany, ...fromSnapshot].sort((a, b) => campaignCreatedAtMs(b) - campaignCreatedAtMs(a));

      cacheHelpers.cacheUserCampaigns(companyId, campaignsData);
      setCampaigns(campaignsData);
      setCampaignsReady(true);
    }, (error) => {
      console.error('Campaigns listener error:', error?.code, error?.message);
      campaignsUnsubRef.current = null; // listener is dead after error

      // DEFENSIVE: Preserve existing campaigns + pending instead of wiping to []
      // This keeps optimistically-added campaigns visible even if the listener fails
      const pending = pendingCampaignsRef.current;
      const pendingForCompany = [];
      pending.forEach((entry) => {
        if (entry.companyId === companyId) {
          pendingForCompany.push(entry.campaign);
        }
      });
      if (pendingForCompany.length > 0) {
        setCampaigns((prev) => {
          // Merge pending campaigns with whatever we already had
          const existingIds = new Set(prev.map(c => c.id));
          const newFromPending = pendingForCompany.filter(c => !existingIds.has(c.id));
          return newFromPending.length > 0
            ? [...newFromPending, ...prev].sort((a, b) => campaignCreatedAtMs(b) - campaignCreatedAtMs(a))
            : prev;
        });
      }
      // Don't wipe campaignsReady — leave as-is so UI doesn't flash skeleton
      // If it was already true (from cache or previous snapshot), keep showing data
      // If it was false, set to true so the UI doesn't stay stuck on skeleton
      setCampaignsReady(true);

      // Retry after a delay (exponential backoff up to 30s)
      const retryDelay = Math.min(3000 * Math.pow(2, Math.random()), 30000);
      campaignsRetryRef.current = setTimeout(() => {
        campaignsRetryRef.current = null;
        if (campaignsListenerCompanyRef.current === companyId) {
          console.log('Retrying campaigns listener for', companyId);
          setupOptimizedCampaignsListener(companyId, { force: true });
        }
      }, retryDelay);
    });
  };

  const updateUserProfile = async (updates) => {
    if (!user) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const updateCompanyProfile = async (updates) => {
    if (!businessProfile) return;
    
    try {
      const companyDocRef = doc(db, 'workspace', businessProfile.id);
      await updateDoc(companyDocRef, updates);
      setBusinessProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating company profile:', error);
      throw error;
    }
  };

  const switchActiveClient = async (clientCompany) => {
    // Multi-workspace was a Team-tier feature; pay-as-you-publish has a single
    // workspace per user. Switching to a non-null client is a no-op.
    if (clientCompany) {
      console.warn('Multi-workspace switching is not available.');
      return;
    }

    try {
      // Clear all caches when reverting to default workspace
      cacheManager.clearAllCaches();

      localStorage.removeItem(SELECTED_COMPANY_KEY);

      if (!userProfile?.companyId) {
        console.error('No original company ID available in user profile');
        return;
      }

      const agencyDoc = await getDoc(doc(db, 'workspace', userProfile.companyId));
      if (agencyDoc.exists()) {
        const businessData = { id: agencyDoc.id, ...agencyDoc.data() };
        setBusinessProfile(businessData);
        setupOptimizedCampaignsListener(agencyDoc.id);
      } else {
        setBusinessProfile(null);
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error reverting workspace:', error);
    }
  };

  /** Optimistically add a newly created campaign so it shows in lists immediately; invalidate cache so stale cache is not shown. */
  const prependCampaign = (campaign) => {
    if (!campaign?.id) return;
    const companyId = campaign.companyId || businessProfile?.id;
    const normalized = { ...campaign, createdAt: campaign.createdAt || new Date() };
    if (companyId) {
      cacheHelpers.invalidateUserCampaigns(companyId);
      pendingCampaignsRef.current.set(campaign.id, { campaign: normalized, companyId });
    }
    flushSync(() => {
      setCampaigns((prev) => {
        const merged = [normalized, ...prev.filter((c) => c.id !== campaign.id)];
        return merged.sort((a, b) => campaignCreatedAtMs(b) - campaignCreatedAtMs(a));
      });
    });
    // If the campaigns listener died (e.g. permission error), revive it now
    // so the new campaign can be confirmed by Firestore
    if (!campaignsUnsubRef.current && companyId && campaignsListenerCompanyRef.current === companyId) {
      setupOptimizedCampaignsListener(companyId, { force: true });
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userProfile,
        businessProfile,
        isOwner,
        loading,
        updateUserProfile,
        updateCompanyProfile,
        campaigns,
        campaignsReady,
        agencyId,
        switchActiveClient,
        onboardingComplete,
        getStudioRedirectPath,
        gameCredits,
        freeSlotInUse,
        isAdmin,
        canPublish,
        openUpgradeModal,
        prependCampaign,
        isLockedOut,
        profileError,
        retryLoadProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Helper function for fetching user profile data (exported for potential reuse)
export const fetchUserProfile = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      return userDoc.data();
    }
    console.error('No such user document found for userId:', userId);
    return null;
  } catch (error) {
    console.error('Error fetching user profile for userId:', userId, error);
    return null;
  }
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/** Sort campaigns by createdAt desc; use from lists so Firestore Timestamp and Date both work. */
export const sortCampaignsByCreatedAt = (list) =>
  [...(list || [])].sort((a, b) => campaignCreatedAtMs(b) - campaignCreatedAtMs(a));
